import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, WS_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useStreaming } from "@/lib/streaming";
import { TIDS } from "@/constants/testIds";
import StreamPlayer from "@/components/StreamPlayer";
import ObsHlsPlayer from "@/components/ObsHlsPlayer";
import PastStreams from "@/components/PastStreams";
import Chat from "@/components/Chat";
import GameSlot from "@/components/games/GameSlot";
import TipModal from "@/components/TipModal";
import ChannelLeaderboard from "@/components/ChannelLeaderboard";
import StreamerModMenu from "@/components/StreamerModMenu";
import ReorderableSidebar from "@/components/ReorderableSidebar";
import { Heart, Coins, AlertTriangle, Trophy, UserPlus, UserCheck, Home } from "lucide-react";
import ChannelSubscribeButton from "@/components/ChannelSubscribeButton";
import { toast } from "sonner";

const SOCIAL_META = {
  steam: { label: "Steam", color: "#1B2838" },
  twitch: { label: "Twitch", color: "#9146FF" },
  kick: { label: "Kick", color: "#53FC18" },
  youtube: { label: "YouTube", color: "#FF0000" },
  tiktok: { label: "TikTok", color: "#FF0050" },
  reddit: { label: "Reddit", color: "#FF4500" },
  facebook: { label: "Facebook", color: "#1877F2" },
  twitter: { label: "X / Twitter", color: "#1DA1F2" },
  instagram: { label: "Instagram", color: "#E4405F" },
  discord: { label: "Discord", color: "#5865F2" },
};

const GAMBLING_CATS = ["Crypto Gambling", "Slots", "Casino Games", "Sports Betting"];

export default function Channel() {
  const { username } = useParams();
  const { user } = useAuth();
  const streaming = useStreaming();
  const [channel, setChannel] = useState(null);
  const [error, setError] = useState("");
  const [ws, setWs] = useState(null);
  const [peerId, setPeerId] = useState(null);
  const [gameState, setGameState] = useState({});
  const [tipOpen, setTipOpen] = useState(false);
  const [followState, setFollowState] = useState({ following: false, follower_count: 0, loading: false });
  // OBS live state — polled from Cloudflare Stream Live lifecycle. When true,
  // the channel is broadcasting via OBS (RTMPS) and we show the HLS player
  // instead of the WebRTC StreamPlayer for viewers.
  const [obsStatus, setObsStatus] = useState({ is_live: false, hls_url: null });
  const wsRef = useRef(null);

  const isStreamer = user && channel && user.username === channel.username;
  const isGamblingStream = channel && GAMBLING_CATS.includes(channel.category);

  useEffect(() => {
    api.get(`/channels/${username}`)
      .then(r => setChannel(r.data))
      .catch(() => setError("Channel not found"));
  }, [username]);

  // Poll Cloudflare-backed OBS live status so we can show the HLS player as
  // soon as the streamer hits "Start Streaming" in OBS. Poll every 4s while
  // offline, extend to 8s while live to save requests. Also updates channel
  // is_live locally so the UI reflects OBS-driven broadcasts.
  useEffect(() => {
    if (!username) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const r = await api.get(`/obs/status/${username}`);
        if (cancelled) return;
        setObsStatus(r.data || { is_live: false, hls_url: null });
        // Auto-flip local channel.is_live so the LIVE chip + placeholder
        // update instantly (the backend also persists this, but we don't
        // want to wait on another /channels fetch).
        if (r.data?.is_live) {
          setChannel(c => (c && !c.is_live ? { ...c, is_live: true } : c));
        }
      } catch { /* noop */ }
    };
    tick();
    timer = setInterval(tick, obsStatus.is_live ? 6000 : 2000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [username, obsStatus.is_live]);

  // Fetch follow state whenever the viewing user or channel changes.
  useEffect(() => {
    if (!user || !channel || user.username === channel.username) {
      // Public follower count for signed-out viewers / self-view
      api.get(`/users/${username}/followers/count`)
        .then(r => setFollowState(s => ({ ...s, follower_count: r.data.follower_count || 0 })))
        .catch(() => {});
      return;
    }
    api.get(`/users/${channel.username}/follow-state`)
      .then(r => setFollowState({ following: !!r.data.following, follower_count: r.data.follower_count || 0, loading: false }))
      .catch(() => {});
  }, [user, channel, username]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Log in to follow this channel."); return; }
    if (!channel) return;
    setFollowState(s => ({ ...s, loading: true }));
    try {
      const url = `/users/${channel.username}/follow`;
      const r = followState.following ? await api.delete(url) : await api.post(url);
      setFollowState({ following: !!r.data.following, follower_count: r.data.follower_count || 0, loading: false });
      toast.success(r.data.following ? `Following @${channel.username}` : `Unfollowed @${channel.username}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Follow failed");
      setFollowState(s => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    if (!channel) return;
    // Pass JWT (if logged in) so the server can trust our identity for
    // security-sensitive actions like guest_request and streamer-role claims.
    const jwt = localStorage.getItem("sc_token");
    const wsUrl = `${WS_BASE}/api/ws/${channel.username}${jwt ? `?token=${encodeURIComponent(jwt)}` : ""}`;
    const sock = new WebSocket(wsUrl);
    wsRef.current = sock;
    sock.onopen = () => {
      const displayName = user?.username || ("Guest" + Math.floor(Math.random()*9999));
      // The channel owner joins as "streamer" so backend room.streamer_peer
      // gets set to their WS. This is required for streamer-only WS actions
      // like `orbdrop_open`, game moderation, etc. WebRTC broadcasting is
      // deprecated (OBS is primary), but the streamer_peer identity is still
      // used for room-scoped permissions.
      sock.send(JSON.stringify({ type: "join", username: displayName, role: isStreamer ? "streamer" : "viewer" }));
    };
    sock.onmessage = (e) => {
      const m = JSON.parse(e.data);
      if (m.type === "welcome") setPeerId(m.peer_id);
      if (m.type === "game_state") setGameState(m.state || {});
      // Whenever the room signals a stream state change, refresh channel data
      // so live badges / titles / viewer counts stay in sync for late-joining viewers.
      if (m.type === "streamer_live" || m.type === "streamer_offline") {
        api.get(`/channels/${channel.username}`).then(r => setChannel(r.data)).catch(() => {});
      }
    };
    setWs(sock);
    // watch-tick heartbeat: every 60s for logged-in non-streamer viewers, awards community XP
    let tickInt = null;
    if (user && !isStreamer) {
      tickInt = setInterval(() => {
        try {
          if (sock.readyState === 1) sock.send(JSON.stringify({ type: "watch_tick" }));
        } catch { /* */ }
      }, 60000);
    }
    return () => { if (tickInt) clearInterval(tickInt); sock.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.id, user?.username]);

  const goLive = async () => {
    if (user && !user.is_verified_streamer && !user.is_admin) {
      alert("You must complete streamer verification (Stripe Identity, 18+) before going live. Go to Account → Streamer Verify.");
      return;
    }
    try { await api.post("/channels/golive", {}); setChannel({ ...channel, is_live: true }); }
    catch (e) { alert(e?.response?.data?.detail || "Failed to go live."); }
  };
  const goOffline = async () => {
    try { await api.post("/channels/offline"); setChannel({ ...channel, is_live: false }); } catch { /* noop */ }
  };

  if (error) return <div className="p-10 text-center text-zinc-400">{error}</div>;
  if (!channel) return <div className="p-10 text-center text-zinc-400">Loading channel...</div>;

  const socials = Object.entries(channel.social_links || {}).filter(([, v]) => v);

  const obsIsLive = obsStatus.is_live || (channel.is_live && channel.live_source === "obs");

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6 grid grid-cols-12 gap-4">
      <div className="col-span-12 lg:col-span-9 space-y-4">
        {/* When OBS is live for this channel, EVERYONE (viewers AND streamer)
            sees the HLS player as the primary video — no more stacked panels.
            The streamer sees the same view as viewers, but with a ~10s HLS
            delay (that's expected and matches what viewers get). */}
        {obsIsLive ? (
          <ObsHlsPlayer
            username={channel.username}
            className="rounded-md overflow-hidden aspect-video border border-white/10"
          />
        ) : (
          <StreamPlayer
            isStreamer={isStreamer}
            channel={channel}
            ws={ws}
            peerId={peerId}
            onGoLive={goLive}
            onOffline={goOffline}
          />
        )}

        {/* Past streams / VOD library. Publicly visible items show for anyone;
            unlisted are hidden from the list but reachable by direct link;
            owner sees everything (including private). */}
        <div className="sc-card p-4" data-testid="channel-past-streams-section">
          <PastStreams channelUsername={channel.username} />
        </div>

        {isGamblingStream && (
          <div className="flex items-start gap-3 p-3 border border-[#F59E0B]/30 bg-[#F59E0B]/5 rounded-sm">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
            <div className="text-xs text-zinc-300">
              <span className="font-bold text-[#F59E0B]">Third-party gambling content.</span> This streamer is broadcasting from external sites (e.g., Stake, Rainbet, Clash.gg). MystiCast does not host gambling. Viewer discretion advised — gamble responsibly. 18+ only.
            </div>
          </div>
        )}

        <div className="flex items-start justify-between flex-wrap gap-3 px-1">
          <div className="flex-1 min-w-0">
            <h1 className="sc-heading text-2xl font-bold">{channel.title}</h1>
            <div className="text-sm text-zinc-400 mt-1">
              <Link to={`/c/${channel.username}`} className="text-white font-medium hover:text-[#FF3366]">{channel.username}</Link> · {channel.category}
            </div>
            {channel.description && <p className="text-sm text-zinc-400 mt-2 max-w-xl">{channel.description}</p>}
            {socials.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-3" data-testid="channel-socials">
                {socials.map(([k, v]) => {
                  const meta = SOCIAL_META[k];
                  if (!meta) return null;
                  const href = v.startsWith("http") ? v : `https://${v}`;
                  return (
                    <a
                      key={k}
                      href={href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-[10px] tracking-widest uppercase font-bold px-2.5 py-1 border rounded-sm hover:bg-white/5 transition"
                      style={{ borderColor: meta.color + "60", color: meta.color }}
                      data-testid={`social-${k}`}
                    >{meta.label}</a>
                  );
                })}
              </div>
            )}
            {channel.contest_rules && (
              <div className="mt-3 p-3 border border-[#10B981]/30 bg-[#10B981]/5 rounded-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-[#10B981]" />
                  <div className="text-xs sc-label text-[#10B981]">Active Contest</div>
                </div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{channel.contest_rules}</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isStreamer && (
              <button data-testid="channel-tip-button" onClick={() => setTipOpen(true)} className="sc-btn-ghost text-sm flex items-center gap-2">
                <Coins className="w-4 h-4 text-[#F59E0B]" /> Tip
              </button>
            )}
            {!isStreamer && (
              <button
                data-testid="channel-follow-button"
                onClick={toggleFollow}
                disabled={followState.loading || !user}
                title={!user ? "Log in to follow" : (followState.following ? "Click to unfollow" : "Follow this channel")}
                className={`text-sm flex items-center gap-2 disabled:opacity-50 ${followState.following ? "sc-btn-ghost" : "sc-btn"}`}
              >
                {followState.following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                {followState.following ? "Following" : "Follow"}
                <span className="sc-mono text-[10px] text-zinc-400 ml-1" data-testid="channel-follower-count">
                  {followState.follower_count.toLocaleString()}
                </span>
              </button>
            )}
            <ChannelSubscribeButton ownerUsername={channel.username} />
            <Link
              to={`/c/${channel.username}`}
              className="sc-btn-ghost text-sm flex items-center gap-2"
              data-testid="channel-visit-home"
              title={`Visit @${channel.username}'s channel page`}
            >
              <Home className="w-4 h-4 text-[color:var(--sc-cyan)]" /> Channel
            </Link>
            <Link
              to={`/c/${channel.username}/leaderboards`}
              className="sc-btn-ghost text-sm flex items-center gap-2"
              data-testid="channel-live-leaderboards-link"
            >
              <Trophy className="w-4 h-4 text-[color:var(--sc-volt)]" /> Leaderboards
            </Link>
          </div>
        </div>

        <GameSlot ws={ws} state={gameState} isStreamer={isStreamer} channelUsername={channel.username} />
      </div>

      <div className="col-span-12 lg:col-span-3 space-y-4">
        <ReorderableSidebar
          storageKey={`channel-${channel.username}`}
          panels={[
            {
              id: "chat",
              title: "Stream Chat",
              defaultOpen: true,
              node: (
                <div className="h-[540px] flex">
                  <Chat ws={ws} peerId={peerId} channel={channel} isStreamer={isStreamer} />
                </div>
              ),
            },
            ...(isStreamer || (user && user.is_admin)
              ? [{
                  id: "mod",
                  title: "Streamer Mod Menu",
                  defaultOpen: false,
                  node: <StreamerModMenu channelUsername={channel.username} canModerate={isStreamer || (user && user.is_admin)} />,
                }]
              : []),
            {
              id: "community",
              title: "Community",
              defaultOpen: true,
              node: (
                <Link
                  to={`/c/${channel.username}`}
                  className="block sc-card p-4 hover:border-[color:var(--sc-volt)]/40 transition text-center"
                  data-testid="channel-side-community-link"
                >
                  <div className="sc-label" style={{ color: "var(--sc-volt)" }}>◤ COMMUNITY HUB</div>
                  <div className="sc-heading text-lg font-black mt-1">Open {channel.username}&apos;s world</div>
                  <div className="text-[10px] sc-mono text-zinc-500 mt-1">Level · Members · Supporters · Challenges · Leaderboard</div>
                </Link>
              ),
            },
            { id: "leaderboards", title: "Leaderboards", defaultOpen: true, node: <ChannelLeaderboard channelUsername={channel.username} /> },
          ]}
        />
      </div>

      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} streamerUsername={channel.username} />
    </div>
  );
}
